import { useEffect, useState } from "react";
import { Loader2, Trash2, CheckCircle2, Clock, Phone, Hash, RefreshCw, Users, ShieldCheck, Save, KeyRound, Download, Mail, Pencil, X, Eye } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const DIVISIONS = ["Sunday Trios", "Tuesday 8-Ball", "Both"];
const editInput = "w-full rounded-md border border-[#E5E7EB] px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

export function SpareListManager({ onPendingChange }) {
  const [spares, setSpares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwd, setPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [access, setAccess] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);

  const apply = (next) => {
    setSpares(next);
    onPendingChange?.(next.filter((x) => !x.approved).length);
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/spares");
      apply(data.spares || []);
    } catch {
      toast.error("Could not load spare list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/spares").then((r) => apply(r.data.spares || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/captains-config").then((r) => setPwd(r.data.password || "")).catch(() => {});
    api.get("/admin/captains-access").then((r) => setAccess(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAccess = async () => {
    try {
      const { data } = await api.get("/admin/captains-access");
      setAccess(data);
    } catch {
      toast.error("Could not load access log");
    }
  };

  const patch = async (s, updates) => {
    try {
      await api.put(`/admin/spares/${s.id}`, updates);
      apply(spares.map((x) => (x.id === s.id ? { ...x, ...updates } : x)));
    } catch {
      toast.error("Update failed");
    }
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setDraft({
      full_name: s.full_name || "",
      phone: s.phone || "",
      email: s.email || "",
      fargo_rating: s.fargo_rating || "",
      divisions: s.divisions || "Both",
    });
  };
  const cancelEdit = () => { setEditId(null); setDraft({}); };
  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveEdit = async (s) => {
    if (!draft.full_name.trim() || !draft.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setSaving(true);
    try {
      const updates = {
        full_name: draft.full_name.trim(),
        phone: draft.phone.trim(),
        email: draft.email.trim(),
        fargo_rating: draft.fargo_rating.trim(),
        divisions: draft.divisions,
      };
      await api.put(`/admin/spares/${s.id}`, updates);
      apply(spares.map((x) => (x.id === s.id ? { ...x, ...updates } : x)));
      toast.success("Spare updated");
      cancelEdit();
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.full_name} from the spare list?`)) return;
    try {
      await api.delete(`/admin/spares/${s.id}`);
      apply(spares.filter((x) => x.id !== s.id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const savePassword = async () => {
    setSavingPwd(true);
    try {
      await api.put("/admin/captains-config", { password: pwd });
      toast.success("Captains password updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update password");
    } finally {
      setSavingPwd(false);
    }
  };

  const exportCsv = () => {
    const rows = [["Name", "Phone", "Email", "Fargo Rating", "Division", "BCA Sanctioned", "Approved", "Submitted"]];
    spares.forEach((s) => rows.push([
      s.full_name || "", s.phone || "", s.email || "", s.fargo_rating || "", s.divisions || "",
      s.bca_sanctioned ? "Yes" : "No", s.approved ? "Yes" : "No",
      s.created_at ? new Date(s.created_at).toLocaleString() : "",
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quintebca-spares-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p className="text-[#555555] inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>;

  return (
    <div className="space-y-6" data-testid="spare-list-manager">
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-5">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-[#1E63C4]" />
          <h3 className="font-heading font-bold">Captains Area Password</h3>
        </div>
        <p className="text-xs text-[#555555] mb-3">Captains use this password to view the approved spare list at <code className="bg-gray-100 px-1 rounded">/captains</code>. Share it only with team captains.</p>
        <div className="flex items-center gap-2">
          <input value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="captains-password-edit" className="rounded-md border border-[#E5E7EB] px-3 py-2 text-sm max-w-[240px] focus:outline-none focus:ring-2 focus:ring-[#1E63C4]" />
          <button onClick={savePassword} disabled={savingPwd || pwd.trim().length < 4} data-testid="captains-password-save" className="inline-flex items-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-[#174e9c] disabled:opacity-60">
            <Save className="h-4 w-4" /> Save
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="captains-access-panel">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-[#1E63C4]" />
            <h3 className="font-heading font-bold">Captains Area Access</h3>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAccess} data-testid="captains-access-refresh" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={() => setAccessOpen((v) => !v)} data-testid="captains-access-toggle" className="text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">
              {accessOpen ? "Hide" : "Show"} details
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-6 mt-3 text-sm">
          <div><span className="text-2xl font-bold text-[#111111]">{access?.unique_users ?? 0}</span><span className="text-[#555555] ml-1.5">unique users</span></div>
          <div><span className="text-2xl font-bold text-[#111111]">{access?.total_accesses ?? 0}</span><span className="text-[#555555] ml-1.5">total logins</span></div>
        </div>

        {accessOpen && (
          <div className="mt-4 space-y-5" data-testid="captains-access-details">
            {(access?.users || []).length === 0 ? (
              <p className="text-sm text-[#555555]">No one has accessed the Captains Area yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-[#E5E7EB]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F9FAF8] text-left text-[#555555]">
                      <th className="px-3 py-2 font-heading font-semibold">Email</th>
                      <th className="px-3 py-2 font-heading font-semibold text-center">Logins</th>
                      <th className="px-3 py-2 font-heading font-semibold">First access</th>
                      <th className="px-3 py-2 font-heading font-semibold">Last access</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {(access?.users || []).map((u) => (
                      <tr key={u.email} data-testid={`captains-access-user-${u.email}`} className="hover:bg-[#F9FAF8]">
                        <td className="px-3 py-2 font-medium text-[#111111]">{u.email}</td>
                        <td className="px-3 py-2 text-center">{u.count}</td>
                        <td className="px-3 py-2 text-[#555555]">{fmtDate(u.first_access)}</td>
                        <td className="px-3 py-2 text-[#555555]">{fmtDate(u.last_access)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(access?.recent || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-[#555555] uppercase tracking-wide mb-2">Recent activity</h4>
                <ul className="space-y-1 text-sm">
                  {(access?.recent || []).map((r) => (
                    <li key={r.id} className="flex items-center gap-2 text-[#333333]">
                      <Mail className="h-3.5 w-3.5 text-[#1E63C4] shrink-0" />
                      <span className="font-medium">{r.email}</span>
                      <span className="text-[#999999]">· {fmtDate(r.accessed_at)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl">Spare List</h2>
          <p className="text-sm text-[#555555]">{spares.length} total · {spares.filter((s) => !s.approved).length} pending approval</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} disabled={spares.length === 0} data-testid="spare-export-csv" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8] disabled:opacity-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
          <button onClick={load} data-testid="spare-refresh-btn" className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-2 hover:bg-[#F9FAF8]">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {spares.length === 0 && <p className="text-[#555555] text-sm">No submissions yet. They&apos;ll appear here as players join the spare list.</p>}

      <ul className="space-y-3">
        {spares.map((s) => (
          <li key={s.id} data-testid={`spare-row-${s.id}`} className="bg-white rounded-lg border border-[#E5E7EB] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            {editId === s.id ? (
              <div className="flex-1 min-w-0 grid gap-2.5 sm:grid-cols-2" data-testid={`spare-edit-${s.id}`}>
                <div>
                  <label className="block text-xs font-medium text-[#555555] mb-1">Full Name</label>
                  <input className={editInput} value={draft.full_name} onChange={(e) => setD("full_name", e.target.value)} data-testid={`spare-edit-name-${s.id}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#555555] mb-1">Phone</label>
                  <input className={editInput} value={draft.phone} onChange={(e) => setD("phone", e.target.value)} data-testid={`spare-edit-phone-${s.id}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#555555] mb-1">Email</label>
                  <input className={editInput} value={draft.email} onChange={(e) => setD("email", e.target.value)} data-testid={`spare-edit-email-${s.id}`} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#555555] mb-1">Fargo Rating</label>
                  <input className={editInput} value={draft.fargo_rating} onChange={(e) => setD("fargo_rating", e.target.value)} data-testid={`spare-edit-fargo-${s.id}`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-[#555555] mb-1">Division</label>
                  <select className={editInput} value={draft.divisions} onChange={(e) => setD("divisions", e.target.value)} data-testid={`spare-edit-division-${s.id}`}>
                    {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[#111111]">{s.full_name}</span>
                  {s.approved ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" /> Approved</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"><Clock className="h-3 w-3" /> Pending</span>
                  )}
                  <span className="inline-flex items-center text-xs font-semibold text-[#1E63C4] bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">{s.divisions}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-sm text-[#555555]">
                  {s.fargo_rating && <span className="inline-flex items-center gap-1"><Hash className="h-3.5 w-3.5 text-[#1E63C4]" /> Fargo {s.fargo_rating}</span>}
                  <a href={`tel:${s.phone}`} className="inline-flex items-center gap-1 hover:text-[#1E63C4]"><Phone className="h-3.5 w-3.5 text-[#1E63C4]" /> {s.phone}</a>
                  {s.email && <a href={`mailto:${s.email}`} className="inline-flex items-center gap-1 hover:text-[#1E63C4]"><Mail className="h-3.5 w-3.5 text-[#1E63C4]" /> {s.email}</a>}
                </div>
                <p className="text-xs text-[#999999] mt-1">Submitted {fmtDate(s.created_at)}</p>
              </div>
            )}
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              {editId === s.id ? (
                <>
                  <button onClick={() => saveEdit(s)} disabled={saving} data-testid={`spare-edit-save-${s.id}`} className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-3 py-1.5 font-semibold hover:bg-[#174e9c] disabled:opacity-60">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                  <button onClick={cancelEdit} data-testid={`spare-edit-cancel-${s.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><X className="h-4 w-4" /> Cancel</button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => patch(s, { bca_sanctioned: !s.bca_sanctioned })}
                    data-testid={`spare-sanctioned-${s.id}`}
                    className={`inline-flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 font-semibold border ${s.bca_sanctioned ? "bg-green-50 border-green-300 text-green-700" : "border-[#E5E7EB] text-[#555555] hover:bg-[#F9FAF8]"}`}
                  >
                    <ShieldCheck className="h-4 w-4" /> BCA: {s.bca_sanctioned ? "Yes" : "No"}
                  </button>
                  {s.approved ? (
                    <button onClick={() => patch(s, { approved: false })} data-testid={`spare-unapprove-${s.id}`} className="text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]">Unapprove</button>
                  ) : (
                    <button onClick={() => patch(s, { approved: true })} data-testid={`spare-approve-${s.id}`} className="inline-flex items-center gap-1.5 text-sm bg-[#1E63C4] text-white rounded-md px-3 py-1.5 font-semibold hover:bg-[#174e9c]"><CheckCircle2 className="h-4 w-4" /> Approve</button>
                  )}
                  <button onClick={() => startEdit(s)} data-testid={`spare-edit-${s.id}`} className="inline-flex items-center gap-1.5 text-sm border border-[#E5E7EB] rounded-md px-3 py-1.5 hover:bg-[#F9FAF8]"><Pencil className="h-4 w-4" /> Edit</button>
                  <button onClick={() => remove(s)} data-testid={`spare-delete-${s.id}`} className="text-red-500 hover:text-red-600 p-1.5" aria-label="Delete spare"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
